import { useEffect, useState, type ComponentProps } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { fetchStoredBankTransactions } from '@/lib/open-banking';
import { format, parseISO } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import type { HomeProfile, TenureType } from './MyHomeTab';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppNativeSelect,
  AppRow,
  AppSectionHeader,
  AppText,
  AppTextArea,
  applySharedHomeBillInferences,
  buildSharedHomeBillDefaults,
  buildSharedHomeBillInferences,
  deriveVehicleServiceDueDate,
  estimateVehicleTaxFromLookup,
  formatSharedHomeBillSchedule,
  getSharedHomeBillDefinition,
  getSharedHomeBillLabel,
  getSharedHomeBillPortalUrl,
  getSharedHomeBillProviderOptions,
  mergeSharedHomeBillRecords,
  normalizeVehicleRegistration,
  SHARED_MOT_GUIDE_COST,
  type SharedHomeVehicleRecord,
  SHARED_HOME_BILL_FREQUENCY_OPTIONS,
  SHARED_HOME_BILL_WEEKDAY_OPTIONS,
  type SharedDvlaVehicleLookupResult,
  type SharedHomeBillInference,
  type SharedHomeBillKey,
  type SharedHomeBillRecord,
  type SharedHomeBillWeekday,
} from '../../../ui-foundation';
import {
  type HomePropertyEnrichmentResult,
  buildPropertyCheckedDateLabel,
  formatHomePropertyValueOrigin,
  invokeHomePropertyEnrichment,
  normalizeHomePropertyLookupInput,
  resolveHomePropertyValueOrigin,
} from '@/lib/home-property-enrichment';
import { repairStoredHomeVehicle } from '@/lib/homeVehicles';
import { invokeDvlaVehicleLookup } from '@/lib/dvla';
import AppDateField from '@/components/AppDateField';

const TENURE_OPTIONS: { value: TenureType; icon: string; label: string }[] = [
  { value: 'renting-private', icon: '🏠', label: 'Renting privately' },
  { value: 'renting-social', icon: '🏢', label: 'Renting social' },
  { value: 'owner-occupier', icon: '🔑', label: 'Owner' },
  { value: 'living-with-family', icon: '👨‍👩‍👧', label: 'Living with family' },
  { value: 'other', icon: '📋', label: 'Other' },
];

const COUNCIL_TAX_BANDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const EPC_RATINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const MANUAL_PROVIDER_VALUE = '__manual__';
const GBP_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

type EditableVehicle = SharedHomeVehicleRecord & {
  draftKey: string;
};

type StoredVehicleRow = {
  id: string;
  registration_number: string;
  make: string | null;
  mot_due_date: string | null;
  mot_status: string | null;
  tax_due_date: string | null;
  tax_status: string | null;
  tax_annual_cost: number | null;
  tax_six_month_cost: number | null;
  tax_monthly_direct_debit_total: number | null;
  tax_monthly_direct_debit_installment: number | null;
  mot_estimated_cost: number | null;
  service_last_date: string | null;
  service_due_date: string | null;
  dvla_synced_at: string | null;
};

function createVehicleDraftKey() {
  return `vehicle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createVehicleDraft(input?: Partial<SharedHomeVehicleRecord>): EditableVehicle {
  return {
    draftKey: createVehicleDraftKey(),
    id: input?.id ?? null,
    registrationNumber: input?.registrationNumber || '',
    make: input?.make ?? null,
    motDueDate: input?.motDueDate ?? null,
    motStatus: input?.motStatus ?? null,
    taxDueDate: input?.taxDueDate ?? null,
    taxStatus: input?.taxStatus ?? null,
    taxAnnualCost: input?.taxAnnualCost ?? null,
    taxSixMonthCost: input?.taxSixMonthCost ?? null,
    taxMonthlyDirectDebitTotal: input?.taxMonthlyDirectDebitTotal ?? null,
    taxMonthlyDirectDebitInstallment: input?.taxMonthlyDirectDebitInstallment ?? null,
    motEstimatedCost: input?.motEstimatedCost ?? SHARED_MOT_GUIDE_COST,
    serviceLastDate: input?.serviceLastDate ?? null,
    serviceDueDate: input?.serviceDueDate ?? null,
    dvlaSyncedAt: input?.dvlaSyncedAt ?? null,
  };
}

function createVehicleDraftFromStored(row: StoredVehicleRow): EditableVehicle {
  return createVehicleDraft({
    id: row.id,
    registrationNumber: row.registration_number,
    make: row.make,
    motDueDate: row.mot_due_date,
    motStatus: row.mot_status,
    taxDueDate: row.tax_due_date,
    taxStatus: row.tax_status,
    taxAnnualCost: row.tax_annual_cost == null ? null : Number(row.tax_annual_cost),
    taxSixMonthCost: row.tax_six_month_cost == null ? null : Number(row.tax_six_month_cost),
    taxMonthlyDirectDebitTotal: row.tax_monthly_direct_debit_total == null ? null : Number(row.tax_monthly_direct_debit_total),
    taxMonthlyDirectDebitInstallment: row.tax_monthly_direct_debit_installment == null ? null : Number(row.tax_monthly_direct_debit_installment),
    motEstimatedCost: row.mot_estimated_cost == null ? SHARED_MOT_GUIDE_COST : Number(row.mot_estimated_cost),
    serviceLastDate: row.service_last_date,
    serviceDueDate: row.service_due_date,
    dvlaSyncedAt: row.dvla_synced_at,
  });
}

function buildLegacyVehicleDrafts(existing: HomeProfile | null) {
  if (!existing?.has_vehicle || !existing.vehicle_registration_number) {
    return [] as EditableVehicle[];
  }

  return [
    createVehicleDraft({
      registrationNumber: existing.vehicle_registration_number,
      make: existing.vehicle_make ?? null,
      motDueDate: existing.vehicle_mot_due_date ?? null,
      motStatus: existing.vehicle_mot_status ?? null,
      taxDueDate: existing.vehicle_tax_due_date ?? null,
      taxStatus: existing.vehicle_tax_status ?? null,
      motEstimatedCost: SHARED_MOT_GUIDE_COST,
      dvlaSyncedAt: existing.vehicle_last_synced_at ?? null,
    }),
  ];
}

interface Props {
  existing: HomeProfile | null;
  onClose: () => void;
  onSaved: () => void;
}

type HomeBillRow = {
  bill_key: SharedHomeBillKey;
  enabled: boolean;
  frequency: SharedHomeBillRecord['frequency'];
  amount: number | null;
  due_day_of_week: SharedHomeBillWeekday | null;
  due_day_of_month: number | null;
  annual_due_date: string | null;
  provider_name: string | null;
  provider_account: string | null;
};

type BankTransactionRow = {
  booking_date: string;
  amount: number;
  description: string | null;
  creditor_name: string | null;
  account_id: string;
};

type BankAccountRow = {
  id: string;
  institution_name: string | null;
};

function buildBillDefaults(existing: HomeProfile | null) {
  return buildSharedHomeBillDefaults({
    tenure: existing?.tenure || null,
    monthlyHousingCost: existing?.monthly_rent ?? null,
    mortgageProvider: existing?.mortgage_provider || null,
    landlordName: existing?.landlord_name || null,
    councilTaxBand: existing?.council_tax_band || null,
    groundRentAmount: existing?.ground_rent_amount ?? null,
    serviceChargeAmount: existing?.service_charge_amount ?? null,
    hasVehicle: existing?.has_vehicle ?? null,
    vehicleRegistrationNumber: existing?.vehicle_registration_number || null,
    vehicleTaxDueDate: existing?.vehicle_tax_due_date || null,
    vehicleMotDueDate: existing?.vehicle_mot_due_date || null,
    utilityGasProvider: existing?.utility_gas_provider || null,
    utilityElectricProvider: existing?.utility_electric_provider || null,
    utilityBroadbandProvider: existing?.utility_broadband_provider || null,
    utilityWaterProvider: existing?.utility_water_provider || null,
    utilityPhoneProvider: existing?.utility_phone_provider || null,
  });
}

function isRenting(t: TenureType | null) {
  return t === 'renting-private' || t === 'renting-social';
}

function isOwner(t: TenureType | null) {
  return t === 'owner-occupier';
}

function buildExistingPropertyLookup(existing: HomeProfile | null): HomePropertyEnrichmentResult | null {
  if (!existing) return null;
  const hasAnyPropertyDetail = Boolean(
    existing.epc_rating
    || existing.epc_score != null
    || existing.epc_expiry_date
    || existing.council_tax_band
    || existing.property_uprn
    || existing.epc_last_checked_at
    || existing.council_tax_last_checked_at
  );

  if (!hasAnyPropertyDetail) return null;

  return {
    normalisedAddress: existing.property_address_normalised || existing.property_lookup_query || existing.address,
    postcode: existing.property_postcode_normalised || null,
    uprn: existing.property_uprn || null,
    candidates: [],
    checkedAt: existing.epc_last_checked_at || existing.council_tax_last_checked_at || null,
    note: null,
    epc: {
      status: (existing.epc_match_status as any) || 'not_checked',
      rating: existing.epc_rating || null,
      score: existing.epc_score ?? null,
      expiryDate: existing.epc_expiry_date || null,
      source: existing.epc_source || null,
      sourceReference: existing.epc_source_reference || null,
      confidence: (existing.epc_confidence as any) || null,
    },
    councilTax: {
      status: (existing.council_tax_match_status as any) || 'not_checked',
      band: existing.council_tax_band || null,
      annualCharge: null,
      authorityName: null,
      source: existing.council_tax_source || null,
      sourceReference: existing.council_tax_source_reference || null,
      confidence: (existing.council_tax_confidence as any) || null,
    },
    bins: {
      status: 'not_checked',
      weekLabel: null,
      source: null,
      sourceReference: null,
      confidence: null,
      entries: [],
      calendarUrl: null,
      gardenCalendarUrl: null,
    },
  };
}

function hasKnownBillProvider(billKey: SharedHomeBillKey, providerName: string | null | undefined) {
  if (!providerName) return false;
  return getSharedHomeBillProviderOptions(billKey).some((option) => option.label === providerName);
}

export default function HomeProfileForm({ existing, onClose, onSaved }: Props) {
  const { user } = useApp();
  const [tenure, setTenure] = useState<TenureType | null>(existing?.tenure ?? null);
  const [address, setAddress] = useState(existing?.address ?? '');
  const [monthlyRent, setMonthlyRent] = useState(existing?.monthly_rent?.toString() ?? '');
  const [depositAmount, setDepositAmount] = useState(existing?.deposit_amount?.toString() ?? '');
  const [tenancyStartDate, setTenancyStartDate] = useState<Date | undefined>(
    existing?.tenancy_start_date ? new Date(existing.tenancy_start_date) : undefined
  );
  const [landlordName, setLandlordName] = useState(existing?.landlord_name ?? '');
  const [landlordEmail, setLandlordEmail] = useState(existing?.landlord_email ?? '');
  const [landlordPhone, setLandlordPhone] = useState(existing?.landlord_phone ?? '');
  const [councilTaxBand, setCouncilTaxBand] = useState(existing?.council_tax_band ?? '');
  const [mortgageProvider, setMortgageProvider] = useState(existing?.mortgage_provider ?? '');
  const [mortgageFixedRateEnd, setMortgageFixedRateEnd] = useState<Date | undefined>(
    existing?.mortgage_fixed_rate_end ? new Date(existing.mortgage_fixed_rate_end) : undefined
  );
  const [epcRating, setEpcRating] = useState(existing?.epc_rating ?? '');
  const [groundRentAmount, setGroundRentAmount] = useState(existing?.ground_rent_amount?.toString() ?? '');
  const [serviceChargeAmount, setServiceChargeAmount] = useState(existing?.service_charge_amount?.toString() ?? '');
  const [hasLawn, setHasLawn] = useState<boolean | null>(existing?.has_lawn ?? null);
  const [hasHouseplants, setHasHouseplants] = useState<boolean | null>(existing?.has_houseplants ?? null);
  const [hasVehicle, setHasVehicle] = useState<boolean | null>(existing?.has_vehicle ?? null);
  const [vehicles, setVehicles] = useState<EditableVehicle[]>(() => buildLegacyVehicleDrafts(existing));
  const [vehiclesLoading, setVehiclesLoading] = useState(Boolean(existing?.has_vehicle));
  const [gasProvider, setGasProvider] = useState(existing?.utility_gas_provider ?? '');
  const [electricProvider, setElectricProvider] = useState(existing?.utility_electric_provider ?? '');
  const [waterProvider, setWaterProvider] = useState(existing?.utility_water_provider ?? '');
  const [broadbandProvider, setBroadbandProvider] = useState(existing?.utility_broadband_provider ?? '');
  const [phoneProvider, setPhoneProvider] = useState(existing?.utility_phone_provider ?? '');
  const [gasContractEnd, setGasContractEnd] = useState<Date | undefined>(
    existing?.utility_gas_contract_end ? new Date(existing.utility_gas_contract_end) : undefined
  );
  const [electricContractEnd, setElectricContractEnd] = useState<Date | undefined>(
    existing?.utility_electric_contract_end ? new Date(existing.utility_electric_contract_end) : undefined
  );
  const [broadbandContractEnd, setBroadbandContractEnd] = useState<Date | undefined>(
    existing?.utility_broadband_contract_end ? new Date(existing.utility_broadband_contract_end) : undefined
  );
  const [phoneContractEnd, setPhoneContractEnd] = useState<Date | undefined>(
    existing?.utility_phone_contract_end ? new Date(existing.utility_phone_contract_end) : undefined
  );
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [bills, setBills] = useState<SharedHomeBillRecord[]>(() => buildBillDefaults(existing));
  const [billInferences, setBillInferences] = useState<Map<SharedHomeBillKey, SharedHomeBillInference>>(new Map());
  const [billsLoading, setBillsLoading] = useState(true);
  const [propertyLookup, setPropertyLookup] = useState<HomePropertyEnrichmentResult | null>(() => buildExistingPropertyLookup(existing));
  const [propertyLookupLoading, setPropertyLookupLoading] = useState(false);
  const [epcManualOverride, setEpcManualOverride] = useState(existing?.epc_value_origin === 'user_confirmed_override');
  const [councilTaxManualOverride, setCouncilTaxManualOverride] = useState(existing?.council_tax_value_origin === 'user_confirmed_override');
  const [manualProviderBills, setManualProviderBills] = useState<Partial<Record<SharedHomeBillKey, boolean>>>({});
  const [saving, setSaving] = useState(false);

  const renting = isRenting(tenure);
  const owner = isOwner(tenure);
  const normalizedPropertyInput = normalizeHomePropertyLookupInput(address);
  const propertyCheckedLabel = buildPropertyCheckedDateLabel(propertyLookup?.checkedAt || null);
  const propertySourceLabels = Array.from(new Set([
    epcRating
      ? (
        epcManualOverride
          ? 'Entered by you'
          : propertyLookup?.epc.status === 'matched' && propertyLookup.epc.rating === epcRating
            ? 'Pulled from property lookup'
            : formatHomePropertyValueOrigin(existing?.epc_value_origin as any)
      )
      : null,
    councilTaxBand
      ? (
        councilTaxManualOverride
          ? 'Entered by you'
          : propertyLookup?.councilTax.status === 'matched' && propertyLookup.councilTax.band === councilTaxBand
            ? 'Pulled from property lookup'
            : formatHomePropertyValueOrigin(existing?.council_tax_value_origin as any)
      )
      : null,
  ].filter((value): value is string => Boolean(value))));
  const propertyStatusNote = propertyLookup?.note
    || (
      propertyLookup?.epc.status === 'ambiguous' || propertyLookup?.councilTax.status === 'ambiguous'
        ? 'More than one property looked possible. Tighten the address or enter the details manually.'
        : propertyLookup?.epc.status === 'unavailable' || propertyLookup?.councilTax.status === 'unavailable'
          ? 'Property lookup could not return a clear match for this address. You can still enter the details manually.'
          : normalizedPropertyInput.reason
    );

  useEffect(() => {
    let active = true;

    async function loadBills() {
      if (!user) {
        if (active) {
          setBills(buildBillDefaults(existing));
          setBillInferences(new Map());
          setBillsLoading(false);
        }
        return;
      }

      setBillsLoading(true);
      const defaults = buildBillDefaults(existing);
      try {
        const [{ data }, bankTransactions, { data: bankAccounts }] = await Promise.all([
          supabase
            .from('home_bill_settings')
            .select('bill_key, enabled, frequency, amount, due_day_of_week, due_day_of_month, annual_due_date, provider_name, provider_account')
            .eq('user_id', user.id),
          fetchStoredBankTransactions(user.id, {
            negativeOnly: true,
            select: 'booking_date, amount, description, creditor_name, account_id',
          }),
          (supabase as any)
            .from('bank_accounts')
            .select('id, institution_name')
            .eq('user_id', user.id),
        ]);

        if (!active) return;

        const storedBills = ((data || []) as HomeBillRow[]).map((row) => ({
          billKey: row.bill_key,
          enabled: row.enabled,
          frequency: row.frequency,
          amount: row.amount == null ? null : Number(row.amount),
          dueDayOfWeek: row.due_day_of_week,
          dueDayOfMonth: row.due_day_of_month,
          annualDueDate: row.annual_due_date,
          providerName: row.provider_name,
          providerAccount: row.provider_account,
        }));
        const baseBills = mergeSharedHomeBillRecords(defaults, storedBills);
        const institutionNames = new Map(((bankAccounts || []) as BankAccountRow[]).map((row) => [row.id, row.institution_name || null]));
        const inferredBills = buildSharedHomeBillInferences(
          ((bankTransactions || []) as BankTransactionRow[]).map((row) => ({
            bookingDate: row.booking_date,
            amount: Number(row.amount || 0),
            description: row.description,
            creditorName: row.creditor_name,
            accountInstitutionName: institutionNames.get(row.account_id) || null,
          })),
          baseBills,
        );

        setBills(applySharedHomeBillInferences(baseBills, inferredBills));
        setBillInferences(inferredBills);
      } catch (error) {
        console.error(error);
        if (active) {
          setBills(defaults);
          setBillInferences(new Map());
        }
      } finally {
        if (active) {
          setBillsLoading(false);
        }
      }
    }

    void loadBills();

    return () => {
      active = false;
    };
  }, [existing, user]);

  useEffect(() => {
    if (hasVehicle === true) return;

    setBills((current) =>
      current.map((bill) =>
        getSharedHomeBillDefinition(bill.billKey)?.vehicleLinked
          ? { ...bill, enabled: false }
          : bill,
      ),
    );
  }, [hasVehicle]);

  useEffect(() => {
    setPropertyLookup((current) => {
      if (!current) return current;
      if (current.normalisedAddress === normalizedPropertyInput.lookupQuery) return current;
      return null;
    });
  }, [normalizedPropertyInput.lookupQuery]);

  useEffect(() => {
    let active = true;

    const loadVehicles = async () => {
      if (!user) {
        if (active) {
          setVehicles(buildLegacyVehicleDrafts(existing));
          setVehiclesLoading(false);
        }
        return;
      }

      const { data, error } = await (supabase as any)
        .from('home_vehicles')
        .select('id, registration_number, make, mot_due_date, mot_status, tax_due_date, tax_status, tax_annual_cost, tax_six_month_cost, tax_monthly_direct_debit_total, tax_monthly_direct_debit_installment, mot_estimated_cost, service_last_date, service_due_date, dvla_synced_at')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (!active) {
        return;
      }

      if (error || !Array.isArray(data) || data.length === 0) {
        setVehicles(buildLegacyVehicleDrafts(existing));
      } else {
        const repairedVehicles = await Promise.all(
          (data as StoredVehicleRow[]).map(async (row) =>
            createVehicleDraft(
              await repairStoredHomeVehicle({
                id: row.id,
                registrationNumber: row.registration_number,
                make: row.make,
                motDueDate: row.mot_due_date,
                motStatus: row.mot_status,
                taxDueDate: row.tax_due_date,
                taxStatus: row.tax_status,
                taxAnnualCost: row.tax_annual_cost == null ? null : Number(row.tax_annual_cost),
                taxSixMonthCost: row.tax_six_month_cost == null ? null : Number(row.tax_six_month_cost),
                taxMonthlyDirectDebitTotal: row.tax_monthly_direct_debit_total == null ? null : Number(row.tax_monthly_direct_debit_total),
                taxMonthlyDirectDebitInstallment: row.tax_monthly_direct_debit_installment == null ? null : Number(row.tax_monthly_direct_debit_installment),
                motEstimatedCost: row.mot_estimated_cost == null ? null : Number(row.mot_estimated_cost),
                serviceLastDate: row.service_last_date,
                serviceDueDate: row.service_due_date,
                dvlaSyncedAt: row.dvla_synced_at,
              }),
            ),
          ),
        );
        setVehicles(repairedVehicles);
      }

      setVehiclesLoading(false);
    };

    void loadVehicles();

    return () => {
      active = false;
    };
  }, [existing, user]);

  useEffect(() => {
    if (!hasVehicle || vehiclesLoading || vehicles.length > 0) {
      return;
    }

    setVehicles([createVehicleDraft()]);
  }, [hasVehicle, vehicles.length, vehiclesLoading]);

  const updateVehicle = (draftKey: string, updates: Partial<EditableVehicle>) => {
    setVehicles((current) =>
      current.map((vehicle) => (vehicle.draftKey === draftKey ? { ...vehicle, ...updates } : vehicle)),
    );
  };

  const addVehicle = () => {
    setVehicles((current) => [...current, createVehicleDraft()]);
  };

  const removeVehicle = (draftKey: string) => {
    setVehicles((current) => current.filter((vehicle) => vehicle.draftKey !== draftKey));
  };

  const updateBill = (billKey: SharedHomeBillKey, updates: Partial<SharedHomeBillRecord>) => {
    setBills((current) =>
      current.map((bill) => (bill.billKey === billKey ? { ...bill, ...updates } : bill)),
    );
  };

  const updateManualProviderMode = (billKey: SharedHomeBillKey, value: boolean) => {
    setManualProviderBills((current) => ({ ...current, [billKey]: value }));
  };

  const handlePropertyCheck = async () => {
    if (!normalizedPropertyInput.canLookup) {
      window.alert(normalizedPropertyInput.reason || 'Add a fuller address before checking property details.');
      return;
    }

    setPropertyLookupLoading(true);
    try {
      const { data, error } = await invokeHomePropertyEnrichment(supabase as any, normalizedPropertyInput.lookupQuery || address);

      if (error || !data) {
        throw new Error(error?.message || 'Property lookup is unavailable right now.');
      }

      setPropertyLookup(data);

      if (data.epc.status === 'matched' && data.epc.rating) {
        setEpcRating(data.epc.rating);
        setEpcManualOverride(false);
      }

      if (data.councilTax.status === 'matched' && data.councilTax.band) {
        setCouncilTaxBand(data.councilTax.band);
        setCouncilTaxManualOverride(false);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Could not check property details right now.');
    } finally {
      setPropertyLookupLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const propertyLookupInput = normalizeHomePropertyLookupInput(address);
      let nextPropertyLookup = propertyLookup;

      if (
        propertyLookupInput.canLookup
        && propertyLookupInput.lookupQuery
        && (!nextPropertyLookup || nextPropertyLookup.normalisedAddress !== propertyLookupInput.lookupQuery)
      ) {
        const { data, error } = await invokeHomePropertyEnrichment(supabase as any, propertyLookupInput.lookupQuery);
        if (!error && data) {
          nextPropertyLookup = data;
          setPropertyLookup(data);
        }
      }

      const existingLookupInput = normalizeHomePropertyLookupInput(
        existing?.property_lookup_query || existing?.property_address_normalised || existing?.address || '',
      );
      const addressChanged = existingLookupInput.lookupQuery !== propertyLookupInput.lookupQuery;
      const effectivePropertyLookup = !addressChanged
        ? (nextPropertyLookup || buildExistingPropertyLookup(existing))
        : (nextPropertyLookup?.normalisedAddress === propertyLookupInput.lookupQuery ? nextPropertyLookup : null);
      const effectiveCouncilTaxBand = (
        councilTaxBand
        || (
          !councilTaxManualOverride
          && effectivePropertyLookup?.councilTax.status === 'matched'
          ? effectivePropertyLookup.councilTax.band || ''
          : ''
        )
      ).trim();
      const effectiveEpcRating = (
        epcRating
        || (
          !epcManualOverride
          && effectivePropertyLookup?.epc.status === 'matched'
          ? effectivePropertyLookup.epc.rating || ''
          : ''
        )
      ).trim();
      const nextEpcOrigin = resolveHomePropertyValueOrigin({
        value: effectiveEpcRating || null,
        previousOrigin: (existing?.epc_value_origin as any) || null,
        lookupValue: effectivePropertyLookup?.epc.rating || null,
        lookupMatched: effectivePropertyLookup?.epc.status === 'matched',
        manualOverride: epcManualOverride,
      });
      const nextCouncilTaxOrigin = resolveHomePropertyValueOrigin({
        value: effectiveCouncilTaxBand || null,
        previousOrigin: (existing?.council_tax_value_origin as any) || null,
        lookupValue: effectivePropertyLookup?.councilTax.band || null,
        lookupMatched: effectivePropertyLookup?.councilTax.status === 'matched',
        manualOverride: councilTaxManualOverride,
      });

      const nextBills = bills.map((bill) => {
        if (hasVehicle === true) {
          return { ...bill };
        }

        return getSharedHomeBillDefinition(bill.billKey)?.vehicleLinked
          ? { ...bill, enabled: false }
          : { ...bill };
      });

      const vehicleDrafts = hasVehicle
        ? vehicles
            .map((vehicle) => ({
              ...vehicle,
              registrationNumber: normalizeVehicleRegistration(vehicle.registrationNumber),
              serviceLastDate: vehicle.serviceLastDate || null,
              serviceDueDate: vehicle.serviceDueDate || null,
            }))
            .filter((vehicle) => Boolean(vehicle.registrationNumber))
        : [];

      if (hasVehicle && vehicleDrafts.length === 0) {
        throw new Error('Add at least one vehicle registration, or switch vehicle to No.');
      }

      const uniqueRegistrations = new Set(vehicleDrafts.map((vehicle) => vehicle.registrationNumber));
      if (uniqueRegistrations.size !== vehicleDrafts.length) {
        throw new Error('Each vehicle registration should only be added once.');
      }

      const lookupErrors: string[] = [];
      const resolvedVehicles: SharedHomeVehicleRecord[] = [];

      for (const vehicle of vehicleDrafts) {
        let lookupResult: SharedDvlaVehicleLookupResult | null = null;
        const { data, error } = await invokeDvlaVehicleLookup(vehicle.registrationNumber);

        if (error || !data) {
          lookupErrors.push(`${vehicle.registrationNumber}: ${error?.message || 'DVLA lookup is unavailable right now.'}`);
        } else {
          lookupResult = data;
        }

        const estimatedTax = lookupResult ? estimateVehicleTaxFromLookup(lookupResult) : null;
        const serviceDueDate = deriveVehicleServiceDueDate(vehicle.serviceLastDate, vehicle.serviceDueDate);

        resolvedVehicles.push({
          id: vehicle.id ?? null,
          registrationNumber: vehicle.registrationNumber,
          make: lookupResult?.make ?? vehicle.make ?? null,
          motDueDate: lookupResult?.motExpiryDate ?? vehicle.motDueDate ?? null,
          motStatus: lookupResult?.motStatus ?? vehicle.motStatus ?? null,
          taxDueDate: lookupResult?.taxDueDate ?? vehicle.taxDueDate ?? null,
          taxStatus: lookupResult?.taxStatus ?? vehicle.taxStatus ?? null,
          taxAnnualCost: estimatedTax?.annual ?? vehicle.taxAnnualCost ?? null,
          taxSixMonthCost: estimatedTax?.sixMonth ?? vehicle.taxSixMonthCost ?? null,
          taxMonthlyDirectDebitTotal: estimatedTax?.monthlyDirectDebitTotal ?? vehicle.taxMonthlyDirectDebitTotal ?? null,
          taxMonthlyDirectDebitInstallment: estimatedTax?.monthlyDirectDebitInstallment ?? vehicle.taxMonthlyDirectDebitInstallment ?? null,
          motEstimatedCost: vehicle.motEstimatedCost ?? SHARED_MOT_GUIDE_COST,
          serviceLastDate: vehicle.serviceLastDate ?? null,
          serviceDueDate,
          dvlaSyncedAt: lookupResult?.generatedAt ?? vehicle.dvlaSyncedAt ?? null,
        });
      }

      const primaryVehicle = resolvedVehicles[0] ?? null;

      nextBills.forEach((bill) => {
        if (!primaryVehicle) {
          return;
        }

        if (bill.billKey === 'car-tax') {
          bill.annualDueDate = primaryVehicle.taxDueDate || bill.annualDueDate;
        }
        if (bill.billKey === 'car-mot') {
          bill.annualDueDate = primaryVehicle.motDueDate || bill.annualDueDate;
        }
        if (bill.billKey === 'car-service') {
          bill.annualDueDate = primaryVehicle.serviceDueDate || bill.annualDueDate;
        }
      });

      const row = {
        user_id: user.id,
        tenure,
        address: address.trim() || null,
        monthly_rent: monthlyRent ? parseFloat(monthlyRent) : null,
        deposit_amount: renting && depositAmount ? parseFloat(depositAmount) : null,
        tenancy_start_date: renting && tenancyStartDate ? format(tenancyStartDate, 'yyyy-MM-dd') : null,
        landlord_name: renting ? landlordName.trim() || null : null,
        landlord_email: renting ? landlordEmail.trim() || null : null,
        landlord_phone: renting ? landlordPhone.trim() || null : null,
        council_tax_band: effectiveCouncilTaxBand || null,
        council_tax_source: effectivePropertyLookup?.councilTax.source || null,
        council_tax_source_reference: effectivePropertyLookup?.councilTax.sourceReference || null,
        council_tax_last_checked_at: effectivePropertyLookup?.checkedAt || null,
        council_tax_match_status: effectivePropertyLookup?.councilTax.status || 'not_checked',
        council_tax_confidence: effectivePropertyLookup?.councilTax.confidence || null,
        council_tax_value_origin: nextCouncilTaxOrigin,
        mortgage_provider: owner ? mortgageProvider.trim() || null : null,
        mortgage_fixed_rate_end: owner && mortgageFixedRateEnd ? format(mortgageFixedRateEnd, 'yyyy-MM-dd') : null,
        epc_rating: effectiveEpcRating || null,
        epc_score: effectivePropertyLookup?.epc.score ?? null,
        epc_expiry_date: effectivePropertyLookup?.epc.expiryDate ?? null,
        epc_source: effectivePropertyLookup?.epc.source || null,
        epc_source_reference: effectivePropertyLookup?.epc.sourceReference || null,
        epc_last_checked_at: effectivePropertyLookup?.checkedAt || null,
        epc_match_status: effectivePropertyLookup?.epc.status || 'not_checked',
        epc_confidence: effectivePropertyLookup?.epc.confidence || null,
        epc_value_origin: nextEpcOrigin,
        ground_rent_amount: owner && groundRentAmount ? parseFloat(groundRentAmount) : null,
        service_charge_amount: owner && serviceChargeAmount ? parseFloat(serviceChargeAmount) : null,
        property_lookup_query: propertyLookupInput.lookupQuery,
        property_uprn: effectivePropertyLookup?.uprn || null,
        property_address_normalised: effectivePropertyLookup?.normalisedAddress || propertyLookupInput.lookupQuery,
        property_postcode_normalised: effectivePropertyLookup?.postcode || propertyLookupInput.postcode,
        has_lawn: hasLawn,
        has_houseplants: hasHouseplants,
        has_vehicle: resolvedVehicles.length > 0,
        vehicle_registration_number: primaryVehicle?.registrationNumber || null,
        vehicle_make: primaryVehicle?.make || null,
        vehicle_mot_due_date: primaryVehicle?.motDueDate || null,
        vehicle_mot_status: primaryVehicle?.motStatus || null,
        vehicle_tax_due_date: primaryVehicle?.taxDueDate || null,
        vehicle_tax_status: primaryVehicle?.taxStatus || null,
        vehicle_last_synced_at: primaryVehicle?.dvlaSyncedAt || null,
        utility_gas_provider: gasProvider.trim() || null,
        utility_electric_provider: electricProvider.trim() || null,
        utility_water_provider: waterProvider.trim() || null,
        utility_broadband_provider: broadbandProvider.trim() || null,
        utility_phone_provider: phoneProvider.trim() || null,
        utility_gas_contract_end: gasProvider.trim() && gasContractEnd ? format(gasContractEnd, 'yyyy-MM-dd') : null,
        utility_electric_contract_end: electricProvider.trim() && electricContractEnd ? format(electricContractEnd, 'yyyy-MM-dd') : null,
        utility_broadband_contract_end: broadbandProvider.trim() && broadbandContractEnd ? format(broadbandContractEnd, 'yyyy-MM-dd') : null,
        utility_phone_contract_end: phoneProvider.trim() && phoneContractEnd ? format(phoneContractEnd, 'yyyy-MM-dd') : null,
        notes: notes.trim() || null,
      };

      if (existing) {
        await supabase.from('home_profiles').update(row as any).eq('id', existing.id);
      } else {
        await supabase.from('home_profiles').insert(row as any);
      }

      if (resolvedVehicles.length > 0) {
        const { data: existingVehicleRows } = await (supabase as any)
          .from('home_vehicles')
          .select('id, registration_number')
          .eq('user_id', user.id);

        const registrations = resolvedVehicles.map((vehicle) => vehicle.registrationNumber);
        const removableIds = (((existingVehicleRows || []) as Array<{ id: string; registration_number: string }>)).filter(
          (vehicle) => !registrations.includes(normalizeVehicleRegistration(vehicle.registration_number)),
        ).map((vehicle) => vehicle.id);

        await (supabase as any).from('home_vehicles').upsert(
          resolvedVehicles.map((vehicle, index) => ({
            user_id: user.id,
            sort_order: index,
            registration_number: vehicle.registrationNumber,
            make: vehicle.make,
            mot_due_date: vehicle.motDueDate,
            mot_status: vehicle.motStatus,
            tax_due_date: vehicle.taxDueDate,
            tax_status: vehicle.taxStatus,
            tax_annual_cost: vehicle.taxAnnualCost,
            tax_six_month_cost: vehicle.taxSixMonthCost,
            tax_monthly_direct_debit_total: vehicle.taxMonthlyDirectDebitTotal,
            tax_monthly_direct_debit_installment: vehicle.taxMonthlyDirectDebitInstallment,
            mot_estimated_cost: vehicle.motEstimatedCost,
            service_last_date: vehicle.serviceLastDate,
            service_due_date: vehicle.serviceDueDate,
            dvla_synced_at: vehicle.dvlaSyncedAt,
          })),
          { onConflict: 'user_id,registration_number' },
        );

        if (removableIds.length > 0) {
          await (supabase as any).from('home_vehicles').delete().in('id', removableIds);
        }
      } else {
        await (supabase as any).from('home_vehicles').delete().eq('user_id', user.id);
      }

      await supabase.from('home_bill_settings').upsert(
        nextBills.map((bill) => ({
          user_id: user.id,
          bill_key: bill.billKey,
          enabled: bill.enabled,
          frequency: bill.frequency,
          amount: bill.amount,
          due_day_of_week: bill.frequency === 'weekly' ? bill.dueDayOfWeek : null,
          due_day_of_month: bill.frequency === 'monthly' ? bill.dueDayOfMonth : null,
          annual_due_date: bill.frequency === 'annual' ? bill.annualDueDate : null,
          provider_name: bill.providerName?.trim() || null,
          provider_account: bill.providerAccount?.trim() || null,
        })),
        { onConflict: 'user_id,bill_key' },
      );

      window.dispatchEvent(new CustomEvent('adulting:data-changed'));

      if (lookupErrors.length > 0) {
        window.alert(`Home details saved. Some vehicle details could not be refreshed:\n\n${lookupErrors.join('\n')}`);
      }

      onSaved();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Could not save your home details right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppCardBody className="animate-fade-in" gap="$lg">
      <AppButton type="button" tone="ghost" size="sm" onPress={onClose} alignSelf="flex-start">
        <ArrowLeft size={16} />
        Back
      </AppButton>

      <AppCard>
        <AppSectionHeader
          title="Home basics"
          subtitle="Start with the core home details Adulting uses for bills, reminders, and admin flows."
        />

        <AppFieldGroup label="Your living situation">
          <AppButtonRow flexWrap="wrap" alignItems="stretch">
            {TENURE_OPTIONS.map((option) => {
              const isSelected = tenure === option.value;
              return (
                <AppButton
                  key={option.value}
                  tone={isSelected ? 'primary' : 'outline'}
                  size="lg"
                  onPress={() => setTenure(option.value)}
                  justifyContent="flex-start"
                  height="auto"
                  py="$md"
                  width="calc(50% - 8px)"
                >
                  <AppText fontSize="$lg" color={isSelected ? '$primaryForeground' : '$color'}>
                    {option.icon}
                  </AppText>
                  <AppText
                    flex={1}
                    fontSize="$sm"
                    fontWeight="700"
                    textAlign="left"
                    color={isSelected ? '$primaryForeground' : '$color'}
                  >
                    {option.label}
                  </AppText>
                </AppButton>
              );
            })}
          </AppButtonRow>
        </AppFieldGroup>

        <InputField
          label="Address"
          value={address}
          onChange={setAddress}
          placeholder="e.g. 14 Maple Drive, Bristol BS1 4AA"
          autoComplete="street-address"
        />

        <CurrencyField
          label="Monthly mortgage / rent (optional)"
          value={monthlyRent}
          onChange={setMonthlyRent}
          placeholder="0.00"
        />

        <AppSectionHeader
          title="Home setup"
          subtitle="Use these to keep the Home routines and vehicle-related admin relevant to the home you actually have."
        />

        <BooleanField label="Do you have a lawn?" value={hasLawn} onChange={setHasLawn} />
        <BooleanField label="Do you have houseplants?" value={hasHouseplants} onChange={setHasHouseplants} />
        <BooleanField label="Do you have a vehicle?" value={hasVehicle} onChange={setHasVehicle} />
        {hasVehicle ? (
          <>
            <AppText fontSize="$xs" color="$colorMuted">
              Adulting will refresh MOT and vehicle-tax dates from DVLA when you save. Insurance renewal stays in Renewals, and service due can be entered directly or estimated from the last service date.
            </AppText>
            {vehiclesLoading ? (
              <AppText fontSize="$xs" color="$colorMuted">
                Loading saved vehicles…
              </AppText>
            ) : null}
            {vehicles.map((vehicle, index) => {
              const estimatedServiceDueDate = deriveVehicleServiceDueDate(vehicle.serviceLastDate, vehicle.serviceDueDate);

              return (
                <AppCard key={vehicle.draftKey} backgroundColor="$mutedBackground" px="$md" py="$sm">
                  <AppFormSection gap="$sm">
                    <AppRow justifyContent="space-between" alignItems="center">
                      <AppText fontSize="$sm" fontWeight="700">Vehicle {index + 1}</AppText>
                      {vehicles.length > 1 ? (
                        <AppButton type="button" tone="ghost" size="sm" onPress={() => removeVehicle(vehicle.draftKey)}>
                          Remove
                        </AppButton>
                      ) : null}
                    </AppRow>
                    <InputField
                      label="Vehicle registration number"
                      value={vehicle.registrationNumber}
                      onChange={(value) => updateVehicle(vehicle.draftKey, { registrationNumber: value })}
                      placeholder="e.g. AB12 CDE"
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                    <DateField
                      label="Last service date (optional)"
                      value={vehicle.serviceLastDate ? new Date(vehicle.serviceLastDate) : undefined}
                      onChange={(value) => updateVehicle(vehicle.draftKey, { serviceLastDate: value ? format(value, 'yyyy-MM-dd') : null })}
                    />
                    <DateField
                      label="Next service due date (optional)"
                      value={vehicle.serviceDueDate ? new Date(vehicle.serviceDueDate) : undefined}
                      onChange={(value) => updateVehicle(vehicle.draftKey, { serviceDueDate: value ? format(value, 'yyyy-MM-dd') : null })}
                    />
                    {estimatedServiceDueDate && !vehicle.serviceDueDate ? (
                      <AppText fontSize="$xs" color="$colorMuted">
                        Estimated next service due: {format(new Date(estimatedServiceDueDate), 'd MMM yyyy')}
                      </AppText>
                    ) : null}
                    {normalizeVehicleRegistration(vehicle.registrationNumber) ? (
                      <AppText fontSize="$xs" color="$colorMuted">
                        {[vehicle.make, normalizeVehicleRegistration(vehicle.registrationNumber)].filter(Boolean).join(' • ')}
                        {vehicle.motDueDate ? ` • MOT ${vehicle.motDueDate}` : ''}
                        {vehicle.taxDueDate ? ` • Tax ${vehicle.taxDueDate}` : ''}
                      </AppText>
                    ) : null}
                  </AppFormSection>
                </AppCard>
              );
            })}
            <AppButton type="button" tone="outline" size="sm" onPress={addVehicle} alignSelf="flex-start">
              Add another vehicle
            </AppButton>
          </>
        ) : null}
      </AppCard>

      {renting && (
        <AppCard>
          <AppSectionHeader
            title="Renting details"
            subtitle="Keep tenancy dates, deposit, and landlord details together."
          />

          <CurrencyField
            label="Deposit paid (optional)"
            value={depositAmount}
            onChange={setDepositAmount}
            placeholder="0.00"
          />

          <DateField
            label="Tenancy start date (optional)"
            value={tenancyStartDate}
            onChange={setTenancyStartDate}
          />

          <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
            Landlord details (optional)
          </AppText>

          <InputField label="Name" value={landlordName} onChange={setLandlordName} placeholder="e.g. John Smith" />
          <InputField
            label="Email"
            value={landlordEmail}
            onChange={setLandlordEmail}
            placeholder="e.g. landlord@email.com"
            type="email"
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
          />
          <InputField
            label="Phone"
            value={landlordPhone}
            onChange={setLandlordPhone}
            placeholder="e.g. 07700 900000"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            spellCheck={false}
          />
        </AppCard>
      )}

      {owner && (
        <AppCard>
          <AppSectionHeader
            title="Ownership details"
            subtitle="Keep mortgage and property charges together."
          />

          <InputField
            label="Mortgage provider"
            value={mortgageProvider}
            onChange={setMortgageProvider}
            placeholder="e.g. Nationwide"
          />

          <DateField
            label="Fixed-rate end date (optional)"
            value={mortgageFixedRateEnd}
            onChange={setMortgageFixedRateEnd}
          />

          <CurrencyField
            label="Ground rent (optional)"
            value={groundRentAmount}
            onChange={setGroundRentAmount}
            placeholder="0.00"
          />

          <CurrencyField
            label="Service charge (optional)"
            value={serviceChargeAmount}
            onChange={setServiceChargeAmount}
            placeholder="0.00"
          />
        </AppCard>
      )}

      <AppCard>
        <AppSectionHeader
          title="Property details"
          subtitle="Check EPC and council tax from property lookup sources, or keep them here manually if the lookup is not available."
        />

        <AppText fontSize="$xs" color="$colorMuted">
          {normalizedPropertyInput.lookupQuery || 'Add the address above first so Adulting can check the right property.'}
        </AppText>

        <AppButton
          type="button"
          tone="outline"
          onPress={handlePropertyCheck}
          disabled={propertyLookupLoading || !normalizedPropertyInput.canLookup}
          alignSelf="flex-start"
        >
          {propertyLookupLoading ? 'Checking property details…' : 'Check property details'}
        </AppButton>

        <AppText fontSize="$xs" color="$colorMuted">
          Checked from property lookup sources when available. This is not a live sync, and it may not be available for every address.
        </AppText>

        {propertyStatusNote ? (
          <AppText fontSize="$xs" color="$colorMuted">
            {propertyStatusNote}
          </AppText>
        ) : null}

        {propertyLookup?.councilTax.annualCharge != null || propertyLookup?.bins.entries.length ? (
          <AppFormSection backgroundColor="$mutedBackground" borderColor="$borderColor" borderWidth={1} borderRadius="$md" p="$sm" gap="$xs">
            {propertyLookup?.councilTax.annualCharge != null ? (
              <AppText fontSize="$xs" color="$colorMuted">
                {propertyLookup.councilTax.authorityName || 'Council tax'} annual charge: {GBP_FORMATTER.format(propertyLookup.councilTax.annualCharge)}
              </AppText>
            ) : null}
            {propertyLookup?.bins.entries.length ? (
              <AppFormSection gap="$xs">
                <AppText fontSize="$xs" fontWeight="700">Next bin collections</AppText>
                {propertyLookup.bins.entries.map((entry) => (
                  <AppText key={`${entry.service}-${entry.nextCollectionDate || 'none'}`} fontSize="$xs" color="$colorMuted">
                    {entry.service}: {entry.nextCollectionDate ? format(parseISO(entry.nextCollectionDate), 'EEE d MMM yyyy') : 'No next date yet'}
                    {entry.scheduleLabel ? ` • ${entry.scheduleLabel}` : ''}
                  </AppText>
                ))}
              </AppFormSection>
            ) : null}
          </AppFormSection>
        ) : null}

        {propertyLookup?.candidates.length ? (
          <AppFormSection backgroundColor="$mutedBackground" borderColor="$borderColor" borderWidth={1} borderRadius="$md" p="$sm" gap="$xs">
            <AppText fontSize="$xs" fontWeight="700">Possible matches</AppText>
            <AppFormSection gap="$xs">
              {propertyLookup.candidates.map((candidate) => (
                <AppText key={`${candidate.address}-${candidate.postcode || ''}`} fontSize="$xs" color="$colorMuted">
                  {candidate.address}
                  {candidate.postcode ? `, ${candidate.postcode}` : ''}
                </AppText>
              ))}
            </AppFormSection>
          </AppFormSection>
        ) : null}

        {(propertySourceLabels.length || propertyCheckedLabel) ? (
          <AppFormSection backgroundColor="$mutedBackground" borderColor="$borderColor" borderWidth={1} borderRadius="$md" p="$sm" gap="$xs">
            {propertySourceLabels.length ? (
              <AppText fontSize="$xs" color="$colorMuted">
                {propertySourceLabels.join(' • ')}
              </AppText>
            ) : null}
            {propertyCheckedLabel ? (
              <AppText fontSize="$xs" color="$colorMuted">
                {propertyCheckedLabel}
              </AppText>
            ) : null}
          </AppFormSection>
        ) : null}

        <AppFieldGroup label="Council tax band">
          <AppButtonRow>
            {COUNCIL_TAX_BANDS.map((band) => (
              <AppButton
                key={band}
                type="button"
                tone={councilTaxBand === band ? 'primary' : 'outline'}
                size="sm"
                onPress={() => {
                  setCouncilTaxBand(councilTaxBand === band ? '' : band);
                  setCouncilTaxManualOverride(true);
                }}
                minWidth={36}
                px="$xs"
              >
                {band}
              </AppButton>
            ))}
          </AppButtonRow>
        </AppFieldGroup>

        <AppFieldGroup label="EPC rating">
          <AppButtonRow>
            {EPC_RATINGS.map((rating) => (
              <AppButton
                key={rating}
                type="button"
                tone={epcRating === rating ? 'primary' : 'outline'}
                size="sm"
                onPress={() => {
                  setEpcRating(epcRating === rating ? '' : rating);
                  setEpcManualOverride(true);
                }}
                minWidth={36}
                px="$xs"
              >
                {rating}
              </AppButton>
            ))}
          </AppButtonRow>
        </AppFieldGroup>

        {(propertyLookup?.epc.score != null || propertyLookup?.epc.expiryDate || propertyLookup?.uprn) ? (
          <AppFormSection backgroundColor="$mutedBackground" borderColor="$borderColor" borderWidth={1} borderRadius="$md" p="$sm" gap="$xs">
            {propertyLookup?.epc.score != null ? (
              <AppText fontSize="$xs" color="$colorMuted">
                EPC score: {propertyLookup.epc.score}
              </AppText>
            ) : null}
            {propertyLookup?.epc.expiryDate ? (
              <AppText fontSize="$xs" color="$colorMuted">
                EPC expiry: {format(new Date(propertyLookup.epc.expiryDate), 'd MMM yyyy')}
              </AppText>
            ) : null}
            {propertyLookup?.uprn ? (
              <AppText fontSize="$xs" color="$colorMuted">
                UPRN: {propertyLookup.uprn}
              </AppText>
            ) : null}
          </AppFormSection>
        ) : null}
      </AppCard>

      <AppCard>
        <AppSectionHeader
          title="Utilities"
          subtitle="Track utility providers so Adulting can catch comparison windows before costs jump."
        />

        <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
          Utility providers (optional)
        </AppText>

        <InputField label="Gas" value={gasProvider} onChange={setGasProvider} placeholder="e.g. British Gas" />
        {gasProvider.trim() && (
          <DateField
            label="Gas contract end date (optional)"
            value={gasContractEnd}
            onChange={setGasContractEnd}
          />
        )}

        <InputField label="Electric" value={electricProvider} onChange={setElectricProvider} placeholder="e.g. Octopus Energy" />
        {electricProvider.trim() && (
          <DateField
            label="Electric contract end date (optional)"
            value={electricContractEnd}
            onChange={setElectricContractEnd}
          />
        )}

        <InputField label="Water" value={waterProvider} onChange={setWaterProvider} placeholder="e.g. Thames Water" />
        <InputField label="Broadband" value={broadbandProvider} onChange={setBroadbandProvider} placeholder="e.g. Sky Broadband" />
        {broadbandProvider.trim() && (
          <DateField
            label="Broadband contract end date (optional)"
            value={broadbandContractEnd}
            onChange={setBroadbandContractEnd}
          />
        )}

        <InputField label="Phone" value={phoneProvider} onChange={setPhoneProvider} placeholder="e.g. EE" />
        {phoneProvider.trim() && (
          <DateField
            label="Phone contract end date (optional)"
            value={phoneContractEnd}
            onChange={setPhoneContractEnd}
          />
        )}

        {(gasProvider.trim() || electricProvider.trim() || broadbandProvider.trim() || phoneProvider.trim()) && (
          <AppText fontSize="$xs" color="$colorMuted">
            Add contract end dates for anything that can roll over so Adulting can flag a price-comparison window before rates jump.
          </AppText>
        )}
      </AppCard>

      <AppCard>
        <AppSectionHeader
          title="Bills"
          subtitle="Switch the standard bills on or off here, then keep the payment pattern and provider account with the home details."
        />

        {billsLoading ? (
          <AppText fontSize="$sm" color="$colorMuted">
            Loading your saved bill settings…
          </AppText>
        ) : null}

        <AppFormSection gap="$sm">
          {bills.map((bill) => (
            <AppCard key={bill.billKey} backgroundColor="$mutedBackground" px="$md" py="$sm">
              <AppFormSection gap="$sm">
                <AppRow alignItems="flex-start">
                  <AppFormSection gap="$xs" flex={1}>
                    <AppText fontSize="$sm" fontWeight="700">{getSharedHomeBillLabel(bill.billKey)}</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      {bill.enabled ? formatSharedHomeBillSchedule(bill) : 'Switched off'}
                    </AppText>
                  </AppFormSection>
                </AppRow>

                <AppFieldGroup label="On or off">
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={bill.enabled ? 'primary' : 'outline'}
                      size="sm"
                      onPress={() => updateBill(bill.billKey, { enabled: true })}
                    >
                      On
                    </AppButton>
                    <AppButton
                      type="button"
                      tone={!bill.enabled ? 'primary' : 'outline'}
                      size="sm"
                      onPress={() => updateBill(bill.billKey, { enabled: false })}
                    >
                      Off
                    </AppButton>
                  </AppButtonRow>
                </AppFieldGroup>

                {bill.enabled ? (
                  <>
                    {billInferences.get(bill.billKey) ? (
                      <AppText fontSize="$xs" color="$colorMuted">
                        {billInferences.get(bill.billKey)?.summary}
                      </AppText>
                    ) : null}

                    <AppFieldGroup label="Frequency">
                      <AppNativeSelect
                        options={SHARED_HOME_BILL_FREQUENCY_OPTIONS.map((option) => ({
                          value: option.value,
                          label: option.label,
                        }))}
                        value={bill.frequency}
                        onChange={(event) => {
                          const nextFrequency = event.target.value as SharedHomeBillRecord['frequency'];
                          updateBill(bill.billKey, {
                            frequency: nextFrequency,
                            dueDayOfWeek: nextFrequency === 'weekly' ? bill.dueDayOfWeek : null,
                            dueDayOfMonth: nextFrequency === 'monthly' ? bill.dueDayOfMonth : null,
                            annualDueDate: nextFrequency === 'annual' ? bill.annualDueDate : null,
                          });
                        }}
                      />
                    </AppFieldGroup>

                    <CurrencyField
                      label="Amount (optional)"
                      value={bill.amount == null ? '' : String(bill.amount)}
                      onChange={(value) => updateBill(bill.billKey, { amount: value ? parseFloat(value) : null })}
                      placeholder="0.00"
                    />

                    {bill.frequency === 'weekly' ? (
                      <AppFieldGroup label="Day of the week paid">
                        <AppNativeSelect
                          options={[
                            { value: '', label: 'Select a day' },
                            ...SHARED_HOME_BILL_WEEKDAY_OPTIONS.map((option) => ({
                              value: option,
                              label: option,
                            })),
                          ]}
                          value={bill.dueDayOfWeek || ''}
                          onChange={(event) => updateBill(bill.billKey, {
                            dueDayOfWeek: (event.target.value || null) as SharedHomeBillWeekday | null,
                          })}
                        />
                      </AppFieldGroup>
                    ) : null}

                    {bill.frequency === 'monthly' ? (
                      <InputField
                        label="Day of the month paid"
                        value={bill.dueDayOfMonth == null ? '' : String(bill.dueDayOfMonth)}
                        onChange={(value) => updateBill(bill.billKey, {
                          dueDayOfMonth: value ? Math.min(31, Math.max(1, Number(value))) : null,
                        })}
                        placeholder="e.g. 1"
                      />
                    ) : null}

                    {bill.frequency === 'annual' ? (
                      <DateField
                        label="Annual due date"
                        value={bill.annualDueDate ? new Date(bill.annualDueDate) : undefined}
                        onChange={(value) => updateBill(bill.billKey, {
                          annualDueDate: value ? format(value, 'yyyy-MM-dd') : null,
                        })}
                      />
                    ) : null}

                    {getSharedHomeBillProviderOptions(bill.billKey).length ? (
                      <AppFieldGroup label="Provider (optional)">
                        <AppNativeSelect
                          options={[
                            { value: '', label: 'Select a provider' },
                            ...getSharedHomeBillProviderOptions(bill.billKey).map((option) => ({
                              value: option.label,
                              label: option.label,
                            })),
                            { value: MANUAL_PROVIDER_VALUE, label: 'Other / manual' },
                          ]}
                          value={
                            (manualProviderBills[bill.billKey] || (bill.providerName ? !hasKnownBillProvider(bill.billKey, bill.providerName) : false))
                              ? MANUAL_PROVIDER_VALUE
                              : (bill.providerName || '')
                          }
                          onChange={(event) => {
                            const nextValue = event.target.value;

                            if (nextValue === MANUAL_PROVIDER_VALUE) {
                              updateManualProviderMode(bill.billKey, true);
                              if (hasKnownBillProvider(bill.billKey, bill.providerName)) {
                                updateBill(bill.billKey, { providerName: null });
                              }
                              return;
                            }

                            updateManualProviderMode(bill.billKey, false);
                            updateBill(bill.billKey, { providerName: nextValue || null });
                          }}
                        />
                      </AppFieldGroup>
                    ) : null}

                    {(manualProviderBills[bill.billKey] || !getSharedHomeBillProviderOptions(bill.billKey).length || (bill.providerName ? !hasKnownBillProvider(bill.billKey, bill.providerName) : false)) ? (
                      <InputField
                        label={getSharedHomeBillProviderOptions(bill.billKey).length ? 'Provider name (optional)' : 'Provider (optional)'}
                        value={bill.providerName || ''}
                        onChange={(value) => updateBill(bill.billKey, { providerName: value || null })}
                        placeholder="e.g. Octopus Energy"
                      />
                    ) : null}

                    {getSharedHomeBillPortalUrl(bill.billKey, bill.providerName) ? (
                      <AppButton
                        type="button"
                        tone="ghost"
                        size="sm"
                        alignSelf="flex-start"
                        onPress={() => window.open(getSharedHomeBillPortalUrl(bill.billKey, bill.providerName) || '', '_blank', 'noopener,noreferrer')}
                      >
                        Open provider link
                      </AppButton>
                    ) : null}

                    <InputField
                      label="Provider account / reference (optional)"
                      value={bill.providerAccount || ''}
                      onChange={(value) => updateBill(bill.billKey, { providerAccount: value || null })}
                      placeholder="e.g. account 12345678"
                    />
                  </>
                ) : null}
              </AppFormSection>
            </AppCard>
          ))}
        </AppFormSection>
      </AppCard>

      <AppCard>
        <AppSectionHeader
          title="Home notes"
          subtitle="Anything useful to keep with the home profile."
        />
        <AppFieldGroup label="Notes (optional)">
          <AppTextArea
            value={notes}
            onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
            placeholder="Any notes about your home..."
            rows={3}
          />
        </AppFieldGroup>
      </AppCard>

      <AppCardBody gap="$sm">
        <AppButton onPress={handleSave} disabled={saving} width="100%">
          {saving ? 'Saving…' : 'Save'}
        </AppButton>
        <AppButton type="button" tone="outline" onPress={onClose} width="100%">
          Cancel
        </AppButton>
      </AppCardBody>
    </AppCardBody>
  );
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  inputMode,
  autoCapitalize,
  spellCheck,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: ComponentProps<typeof AppInput>['type'];
  autoComplete?: ComponentProps<typeof AppInput>['autoComplete'];
  inputMode?: ComponentProps<typeof AppInput>['inputMode'];
  autoCapitalize?: ComponentProps<typeof AppInput>['autoCapitalize'];
  spellCheck?: ComponentProps<typeof AppInput>['spellCheck'];
}) {
  return (
    <AppFieldGroup label={label}>
      <AppInput
        tag="input"
        type={type}
        value={value}
        onChange={(event) => onChange((event.target as HTMLInputElement).value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoCapitalize={autoCapitalize}
        spellCheck={spellCheck}
      />
    </AppFieldGroup>
  );
}

function CurrencyField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <AppFieldGroup label={label}>
      <AppFormSection gap={0} position="relative">
        <AppText
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          fontSize="$sm"
          color="$colorMuted"
        >
          £
        </AppText>
        <AppInput
          tag="input"
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange((event.target as HTMLInputElement).value)}
          placeholder={placeholder}
          style={{ paddingLeft: 28 }}
        />
      </AppFormSection>
    </AppFieldGroup>
  );
}

function BooleanField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (value: boolean | null) => void;
}) {
  return (
    <AppFieldGroup label={label}>
      <AppButtonRow>
        <AppButton
          type="button"
          tone={value === true ? 'primary' : 'outline'}
          size="sm"
          onPress={() => onChange(value === true ? null : true)}
        >
          Yes
        </AppButton>
        <AppButton
          type="button"
          tone={value === false ? 'primary' : 'outline'}
          size="sm"
          onPress={() => onChange(value === false ? null : false)}
        >
          No
        </AppButton>
      </AppButtonRow>
    </AppFieldGroup>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: Date;
  onChange: (value: Date | undefined) => void;
}) {
  return (
    <AppDateField
      label={label}
      value={value ? format(value, 'yyyy-MM-dd') : ''}
      onChange={(nextValue) => onChange(nextValue ? new Date(nextValue) : undefined)}
    />
  );
}
